import React from 'react';
import logo from '../components/logos/logo.png';

const APP_URL = 'https://app.ai4us.net';
const SUPPORT_EMAIL = 'support@ai4us.net';

const IconMail = () => (
	<svg
		xmlns='http://www.w3.org/2000/svg'
		width='18'
		height='18'
		viewBox='0 0 24 24'
		fill='none'
		stroke='currentColor'
		strokeWidth='2'
		strokeLinecap='round'
		strokeLinejoin='round'
		aria-hidden='true'>
		<path d='M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z' />
		<polyline points='22,6 12,13 2,6' />
	</svg>
);

const IconExternal = () => (
	<svg
		xmlns='http://www.w3.org/2000/svg'
		width='14'
		height='14'
		viewBox='0 0 24 24'
		fill='none'
		stroke='currentColor'
		strokeWidth='2'
		strokeLinecap='round'
		strokeLinejoin='round'
		aria-hidden='true'>
		<path d='M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6' />
		<polyline points='15 3 21 3 21 9' />
		<line x1='10' y1='14' x2='21' y2='3' />
	</svg>
);

const IconArrowRight = () => (
	<svg
		xmlns='http://www.w3.org/2000/svg'
		width='18'
		height='18'
		viewBox='0 0 24 24'
		fill='none'
		stroke='currentColor'
		strokeWidth='2.25'
		strokeLinecap='round'
		strokeLinejoin='round'
		aria-hidden='true'>
		<line x1='5' y1='12' x2='19' y2='12' />
		<polyline points='12 5 19 12 12 19' />
	</svg>
);

const linkClass = 'text-light text-decoration-none footer-link';

const Footer = () => {
	const year = new Date().getFullYear();
	return (
		<footer
			className='bg-dark text-light pt-5 mt-5'
			style={{
				background: 'linear-gradient(180deg, #111418 0%, #0a0c10 100%)',
				borderTop: '1px solid rgba(255,255,255,0.06)',
			}}>
			<style>{`
				.footer-link { transition: color 0.15s ease, opacity 0.15s ease; opacity: 0.85; }
				.footer-link:hover { opacity: 1; color: #ffffff; }
				.footer-cta-card {
					background: linear-gradient(135deg, rgba(99,102,241,0.18) 0%, rgba(56,189,248,0.12) 100%);
					border: 1px solid rgba(255,255,255,0.08);
					border-radius: 14px;
				}
				.footer-heading {
					font-size: 0.78rem;
					letter-spacing: 0.08em;
					color: rgba(255,255,255,0.55);
				}
				.footer-icon-link {
					display: inline-flex;
					align-items: center;
					justify-content: center;
					width: 36px;
					height: 36px;
					border-radius: 8px;
					border: 1px solid rgba(255,255,255,0.12);
					color: #fff;
					transition: background 0.15s ease, border-color 0.15s ease;
				}
				.footer-icon-link:hover {
					background: rgba(255,255,255,0.08);
					border-color: rgba(255,255,255,0.25);
					color: #fff;
				}
			`}</style>
			<div className='container'>
				<div className='footer-cta-card p-4 p-md-5 mb-5'>
					<div className='row align-items-center gy-3'>
						<div className='col-lg-8'>
							<h3 className='h4 fw-bold mb-2'>
								Ready to build with AI?
							</h3>
							<p className='mb-0 text-light' style={{ opacity: 0.8 }}>
								Train and ship a custom model in minutes — no code required.
							</p>
						</div>
						<div className='col-lg-4 text-lg-end'>
							<a
								href={`${APP_URL}/newModel`}
								target='_blank'
								rel='noreferrer'
								className='btn btn-light btn-lg fw-bold px-4 d-inline-flex align-items-center gap-2'>
								Build a model <IconArrowRight />
							</a>
						</div>
					</div>
				</div>

				<div className='row gy-4 pb-4'>
					<div className='col-lg-4 col-md-12'>
						<a
							href='/'
							aria-label='ai4us home'
							className='d-inline-block mb-3'>
							<img src={logo} alt='ai4us' style={{ height: 44 }} />
						</a>
						<p className='text-light mb-4' style={{ opacity: 0.75, maxWidth: 360 }}>
							AI for everyone. Build, train, and ship models without writing
							code — from your first prototype to production.
						</p>
						<div className='d-flex gap-2'>
							<a
								href={`mailto:${SUPPORT_EMAIL}`}
								className='footer-icon-link'
								aria-label='Email support'>
								<IconMail />
							</a>
							<a
								href={APP_URL}
								target='_blank'
								rel='noreferrer'
								className='footer-icon-link'
								aria-label='Open the ai4us app'>
								<IconExternal />
							</a>
						</div>
					</div>

					<div className='col-lg-2 col-6 col-md-3'>
						<h6 className='footer-heading text-uppercase fw-bold mb-3'>
							Product
						</h6>
						<ul className='list-unstyled mb-0'>
							<li className='mb-2'>
								<a href='#home' className={linkClass}>
									Overview
								</a>
							</li>
							<li className='mb-2'>
								<a href='#how-it-works' className={linkClass}>
									How it works
								</a>
							</li>
							<li className='mb-2'>
								<a href='#demos' className={linkClass}>
									Features
								</a>
							</li>
							<li className='mb-2'>
								<a href='#examples' className={linkClass}>
									Examples
								</a>
							</li>
						</ul>
					</div>

					<div className='col-lg-3 col-6 col-md-3'>
						<h6 className='footer-heading text-uppercase fw-bold mb-3'>
							Resources
						</h6>
						<ul className='list-unstyled mb-0'>
							<li className='mb-2'>
								<a href='#faq' className={linkClass}>
									FAQ
								</a>
							</li>
							<li className='mb-2'>
								<a
									href={`${APP_URL}/newModel`}
									target='_blank'
									rel='noreferrer'
									className={linkClass}>
									Build a model
								</a>
							</li>
							<li className='mb-2'>
								<a
									href={APP_URL}
									target='_blank'
									rel='noreferrer'
									className={linkClass}>
									Sign in
								</a>
							</li>
							<li className='mb-2'>
								<a href='#partners' className={linkClass}>
									Partners
								</a>
							</li>
						</ul>
					</div>

					<div className='col-lg-3 col-md-6'>
						<h6 className='footer-heading text-uppercase fw-bold mb-3'>
							Get in touch
						</h6>
						<p className='mb-2' style={{ opacity: 0.85 }}>
							Questions, feedback, or partnership ideas? We respond
							within one business day.
						</p>
						<a
							href={`mailto:${SUPPORT_EMAIL}`}
							className='d-inline-flex align-items-center gap-2 fw-semibold text-light text-decoration-none'>
							<IconMail />
							{SUPPORT_EMAIL}
						</a>
					</div>
				</div>

				<hr className='border-secondary my-0' style={{ opacity: 0.15 }} />

				<div className='py-4 d-flex flex-column flex-md-row justify-content-between align-items-md-center small'>
					<div style={{ opacity: 0.6 }}>
						&copy; {year} ai4us. All rights reserved.
					</div>
					<div
						className='mt-2 mt-md-0 d-flex flex-wrap gap-3'
						style={{ opacity: 0.7 }}>
						<span>AI For Everyone</span>
						<span aria-hidden='true'>·</span>
						<a
							href={`mailto:${SUPPORT_EMAIL}?subject=Privacy%20inquiry`}
							className='text-light text-decoration-none'>
							Privacy
						</a>
						<a
							href={`mailto:${SUPPORT_EMAIL}?subject=Terms%20inquiry`}
							className='text-light text-decoration-none'>
							Terms
						</a>
					</div>
				</div>
			</div>
		</footer>
	);
};

export default Footer;
